import type React from 'react';

import { Button, Card, CardBody, Link, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useLazyLoadedListFocus } from '#src/app/shared/hooks/focus';

import { StateCodeFilter } from './components/StateCodeFilter';
import { useFilterableLocations } from './hooks';

export const FilterableLocations: React.FC = () => {
	const {
		data: { locations, hasNextPage, nextPageLoading, totalCount },
		actions: { loadMore },
	} = useFilterableLocations();
	const { translatePluralHtml, localizeNumber } = useI18n();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	return (
		<div
			ref={containerRef}
			data-testid="filterable-list-section"
			className="text-typography-strong flex flex-col gap-8"
		>
			<StateCodeFilter />
			<div className="space-y-6">
				<Text as="h2" variant="heading" size="small" fontWeight="light" data-qa="local-sitemap-locations-count">
					<ClientRender
						placeholder={
							<Translate value="corgi.location_page_sitemap.location_list.title" replacements={{ count: totalCount }} />
						}
					>
						{() => (
							<Translate
								value="corgi.location_page_sitemap.location_list.title"
								replacements={{ count: localizeNumber(totalCount) }}
							/>
						)}
					</ClientRender>
				</Text>

				<div className="grid w-full gap-6 md:grid-cols-4">
					{locations.map((location, index) => (
						<div key={location.id} className="h-full">
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.location_page_sitemap.location_list.fetched_more"
										count={locations.length - lastEvent.previousItemsCount}
										replacements={{ count: locations.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<Card
								ref={getListItemRef(index)}
								aria-label={location.displayName}
								as={Link}
								className="hover:elevation-low h-full w-full rounded-xl! [--card-padding:1.5rem] [&>span]:no-underline"
								data-qa={`${location.slug}-card`}
								href={`/local/${encodeURIComponent(location.slug)}?source_location=local-sitemap`}
							>
								<CardBody>
									<Text as="div" className="block font-bold underline">
										{location.displayName}
									</Text>

									<Text as="div" className="text-typography-strong mt-2 block">
										{translatePluralHtml(
											'corgi.location_page_sitemap.location_list.card_signatures_count',
											location.totalSignatureCount,
											{ count: location.totalSignatureCountLocalized },
										)}
									</Text>
								</CardBody>
							</Card>
						</div>
					))}
				</div>
			</div>

			<div className="self-center">
				{nextPageLoading && (
					<>
						<div className="sr-only" role="alert">
							<Translate value="corgi.location_page_sitemap.location_list.fetching_more" />
						</div>
						{/* TODO: tomato loading state */}
						<Spinner className="mx-auto mt-6" ref={loadingRef} />
					</>
				)}
				{!nextPageLoading && hasNextPage && (
					<Button
						size="small"
						variant="secondary"
						data-qa="explore-petitions-show-more-button"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: locations.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.topic_page.petition_section.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
